import React, { createContext, useContext } from 'react';
import { AnimatePresence } from 'motion/react';
import { useBlockDismissTimer } from '@/features/ai-agents/chat/block-status-utils';
import {
  BlockStatusList,
  BlockStatusRow,
} from '@/features/ai-agents/chat/block-status-row';

export interface CustomEventPayload {
  content: string;
  loading: boolean;
  tool_call_id: string;
  agent_name: string;
}

export const parseCustomEventPayload = (
  raw: unknown,
): CustomEventPayload | null => {
  try {
    const parsed = typeof raw === `string` ? JSON.parse(raw) : raw;
    if (typeof parsed?.content !== `string`) return null;
    return {
      content: parsed.content,
      loading: parsed.loading ?? false,
      tool_call_id: parsed.tool_call_id ?? ``,
      agent_name: parsed.agent_name ?? ``,
    };
  } catch {
    return null;
  }
};

export const extractCustomEventKind = (className?: string): string | null => {
  if (!className) return null;
  const prefix = `language-vsly/json/custom-event/`;
  const match = className.split(` `).find((cls) => cls.startsWith(prefix));
  if (!match) return null;
  return match.slice(prefix.length) || null;
};

/**
 * Computes which custom event blocks are "completed" — meaning non-empty
 * content has been streamed after the block's closing fence, or streaming
 * has finished. Keyed by tool_call_id.
 */
export const computeProgressCompletionMap = (
  markdown: string,
  isStreamingMessage: boolean,
): Record<string, boolean> => {
  const regex = /```vsly\/json\/custom-event\/[^\n]+\n([\s\S]*?)```/g;
  const result: Record<string, boolean> = {};
  let match: RegExpExecArray | null;

  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(markdown)) !== null) {
    try {
      const payload = JSON.parse(match[1]);
      const key = payload.tool_call_id || `ce-${match.index}`;
      const afterBlock = markdown.slice(match.index + match[0].length);
      result[key] = afterBlock.trim().length > 0 || !isStreamingMessage;
    } catch {
      // skip malformed blocks
    }
  }

  return result;
};

const CompletionMapContext = createContext<Record<string, boolean>>({});

export const CustomEventCompletionProvider = CompletionMapContext.Provider;

const ProgressCustomEvent = ({
  payload,
  completed,
}: {
  payload: CustomEventPayload;
  completed: boolean;
}) => <BlockStatusRow label={payload.content} completed={completed} />;

export const CustomEventBlock = ({
  kind,
  children,
  isLatestMessage,
}: {
  kind: string;
  children: unknown;
  isLatestMessage: boolean;
}) => {
  const completionMap = useContext(CompletionMapContext);
  const raw = Array.isArray(children) ? children[0] : children;
  const payload = parseCustomEventPayload(raw);
  const key = payload?.tool_call_id ?? ``;

  const isCompleted = completionMap[key] ?? !isLatestMessage;

  const dismissed = useBlockDismissTimer(isLatestMessage && isCompleted, false);

  if (!payload) return null;
  if (isLatestMessage && dismissed) return null;

  if (kind === `PROGRESS`) {
    return (
      <BlockStatusList>
        <AnimatePresence>
          <ProgressCustomEvent payload={payload} completed={isCompleted} />
        </AnimatePresence>
      </BlockStatusList>
    );
  }

  return (
    <BlockStatusList>
      <AnimatePresence>
        <BlockStatusRow label={payload.content} completed={isCompleted} />
      </AnimatePresence>
    </BlockStatusList>
  );
};
